import {
  Box,
  Button,
  Flex,
  FormLabel,
  Link,
  Switch,
  Text,
  VisuallyHidden,
} from "@chakra-ui/react";
import { FieldArray, useField, useFormikContext } from "formik";
import React from "react";
import { RiAddCircleLine } from "react-icons/ri";
import { GenericError } from "src/components/Feedback/GenericError";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import { SearchSelect } from "src/components/Inputs/SearchSelect";
import { Item } from "src/components/Inputs/SearchSelect/SearchSelect";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { WithFeatureFlags } from "src/components/Permissions/WithFeatureFlags";
import { WithRequiredHasuraRoles } from "src/components/Permissions/WithRequiredHasuraRoles";
import { SegmentedControl } from "src/components/SegmentedControl";
import { HighlightedText } from "src/components/Text/HighlightedText";
import * as Draft from "src/scenes/orgAdmin/formTemplates/types/draft";
import * as AF from "src/types/formTemplate";
import { HasuraRole } from "src/types/hasuraRole";
import * as RD from "src/types/remoteData";
import { SchoolItem } from "../QuestionForm/types";
import {
  findRelatedQuestionVisibilityByAnswerId,
  FormikType,
  NewRule,
  RuleSchema,
  SchoolSelectionItem,
  toAnswerOptions,
  toQuestionItems,
} from "./helpers";
import { QuestionVisibilityJSONEditor } from "./JSONEditor";

type Props = {
  sectionType: AF.SectionType;
  questionId: string;
  otherQuestions: readonly Draft.Question[];
  schools: RD.RemoteData<unknown, SchoolItem[]>;
};
export const QuestionVisibility: React.FC<Props> = (props) => {
  return (
    <WithFeatureFlags
      featureFlags={["question-visibility-admin"]}
      notEnabled={
        <WithRequiredHasuraRoles roles={[HasuraRole.ADMIN]}>
          <QuestionVisibilityJSONEditor questionId={props.questionId} />
        </WithRequiredHasuraRoles>
      }
    >
      {QuestionVisibilityInternal(props)}
    </WithFeatureFlags>
  );
};

const QuestionVisibilityInternal: React.FC<Props> = ({
  sectionType,
  otherQuestions,
  schools: schoolsRD,
}) => {
  const formik = useFormikContext<FormikType>();
  const formikValue = formik.values.questionVisibility;

  if (formikValue.switch !== "checked") {
    return null;
  }

  const lastRule = formikValue.rules[formikValue.rules.length - 1];
  const canAddQuestion = RuleSchema.safeParse(lastRule).success;
  return (
    <Flex direction="column" gap="6">
      <FieldArray name="questionVisibility.rules">
        {(arrayHelpers) => {
          return (
            <Flex direction="column" gap="4">
              <Flex direction="column" gap="2">
                <Text fontSize="sm" fontWeight="medium">
                  Conditional reveal
                </Text>
                <Text fontSize="sm" fontWeight="normal" color="gray.600">
                  {sectionType === "PreRankingSection"
                    ? `Choose a question and at least one answer that will make this
                    question appear when families fill out the form. You can pick
                    any single select or multi select question that has keys.`
                    : `Choose a question and at least one answer that will make this
                    question appear when families fill out the form. You can pick
                    any single select or multi select question that has keys. You
                    can also make this question show up based on which school they
                    pick by looking for "School selection" below.`}
                </Text>
                {formikValue.rules.map((rule, ruleIndex) => {
                  const isLastRule = ruleIndex === formikValue.rules.length - 1;
                  return (
                    <Flex direction="column" key={ruleIndex} gap="4">
                      <RemoteDataView
                        remoteData={schoolsRD}
                        error={() => <GenericError />}
                      >
                        {(schools) => (
                          <>
                            <Rule
                              sectionType={sectionType}
                              otherQuestions={otherQuestions}
                              schools={schools}
                              ruleIndex={ruleIndex}
                              onQuestionKeyChange={async (
                                key: string | undefined
                              ) => {
                                await formik.setFieldValue(
                                  `questionVisibility.rules[${ruleIndex}].questionKey`,
                                  key
                                );
                                await formik.setFieldValue(
                                  `questionVisibility.rules[${ruleIndex}].optionKeys`,
                                  []
                                );
                                if (key === undefined) {
                                  if (formikValue.rules.length > 1) {
                                    arrayHelpers.remove(ruleIndex);
                                  }
                                }
                              }}
                            />
                            {!isLastRule ? (
                              <Box width="8rem" alignSelf="center">
                                <SegmentedControl
                                  options={[
                                    { label: "And", value: "and" },
                                    { label: "Or", value: "or" },
                                  ]}
                                  value={formikValue.logicalOperator}
                                  onChange={async (value) => {
                                    await formik.setFieldValue(
                                      "questionVisibility.logicalOperator",
                                      value
                                    );
                                  }}
                                />
                              </Box>
                            ) : null}
                          </>
                        )}
                      </RemoteDataView>
                    </Flex>
                  );
                })}
              </Flex>
              {canAddQuestion ? (
                <Button
                  variant="outline"
                  size="xs"
                  colorScheme="gray"
                  alignSelf="flex-start"
                  leftIcon={<RiAddCircleLine />}
                  onClick={() => {
                    arrayHelpers.push(NewRule);
                  }}
                >
                  Add question
                </Button>
              ) : null}
            </Flex>
          );
        }}
      </FieldArray>
    </Flex>
  );
};

type RuleProps = {
  sectionType: AF.SectionType;
  otherQuestions: readonly Draft.Question[];
  schools: SchoolItem[];
  ruleIndex: number;
  onQuestionKeyChange: (key: string | undefined) => Promise<void>;
};
const Rule: React.FC<RuleProps> = ({
  sectionType,
  otherQuestions,
  schools,
  ruleIndex,
  onQuestionKeyChange,
}) => {
  const formik = useFormikContext<FormikType>();
  const formikValue = formik.values.questionVisibility;
  if (formikValue.switch === "disabled") {
    return null;
  }

  const questions = toQuestionItems(sectionType, otherQuestions);

  const rule = formikValue.rules[ruleIndex] ?? NewRule;
  const selectedId = rule.questionKey;
  const selectedQuestion =
    otherQuestions.find((q) => q.id === selectedId) ?? null;
  const missingQuestion =
    selectedId !== undefined &&
    selectedId !== "" &&
    selectedId !== SchoolSelectionItem.key &&
    selectedQuestion === null;

  const options = toAnswerOptions(selectedId, otherQuestions, schools);

  const initialOptions = rule.optionKeys.flatMap((optionKey) => {
    const option = options?.find((o) => o.id === optionKey);
    if (option === undefined) {
      return [];
    }
    return [option];
  });

  const missingAnswers =
    rule.optionKeys.length > 0 && initialOptions.length === 0;

  const selectedItem =
    questions.find((item) => item.key === selectedId) ?? null;

  const optionKeysName = `questionVisibility.rules[${ruleIndex}].optionKeys`;
  const optionKeysMeta = formik.getFieldMeta(optionKeysName);

  const questionKeyName = `questionVisibility.rules[${ruleIndex}].questionKey`;
  const questionKeyMeta = formik.getFieldMeta(questionKeyName);

  return (
    <Flex direction="column" gap="2">
      <SearchSelect
        placeholder="Search for a question or school selection"
        label={<VisuallyHidden>Question</VisuallyHidden>}
        items={questions}
        onChange={async (item: Item | null) => {
          await onQuestionKeyChange(item?.key ?? undefined);
        }}
        selectedItem={selectedItem}
        error={
          missingQuestion
            ? "Revealing question was removed or changed, pick a different question to conditionally reveal this question."
            : questionKeyMeta.error
        }
      />
      {options !== null ? (
        <MultiSelectInput<Option>
          validateSelectedItems
          label={<VisuallyHidden>Answers</VisuallyHidden>}
          options={options}
          size="md"
          placeholder={
            selectedId === SchoolSelectionItem.key
              ? "Please select one of multiple schools"
              : "Please select one or multiple answers"
          }
          initialValues={initialOptions}
          onChange={async (items) => {
            await formik.setFieldValue(
              optionKeysName,
              items.map((i) => i.id)
            );
          }}
          renderDropdownItem={function (
            item: Option,
            keyword: string
          ): React.ReactElement {
            return (
              <HighlightedText text={item.label} keyword={keyword} invert />
            );
          }}
          error={
            missingAnswers
              ? "Revealing answer was removed or changed, pick a different answer to conditionally reveal this question."
              : optionKeysMeta.error
          }
        />
      ) : null}
    </Flex>
  );
};

type SwitchState = "checked" | "disabled" | undefined;
type SwitchProps = {
  questionId: string;
};
export const QuestionVisibilitySwitch: React.FC<SwitchProps> = (props) => {
  return (
    <WithFeatureFlags featureFlags={["question-visibility-admin"]}>
      {QuestionVisibilitySwitchInternal(props)}
    </WithFeatureFlags>
  );
};

const QuestionVisibilitySwitchInternal: React.FC<SwitchProps> = ({
  questionId,
}) => {
  const fieldName = `questionVisibility.switch`;
  const id = `${fieldName}.${questionId}`;
  const [field, , helper] = useField<SwitchState>(fieldName);
  return (
    <>
      <FormLabel htmlFor={id} display="flex" flexDirection="row" gap="3">
        <Switch
          id={id}
          isChecked={field.value === "checked" ? true : false}
          onChange={async () => {
            await helper.setValue(
              field.value === "checked" ? "disabled" : "checked"
            );
          }}
        />
        <Box>Conditional reveal</Box>
      </FormLabel>
    </>
  );
};

type RelatedProps = {
  questionId: string;
  answerId: string;
  otherQuestions: readonly Draft.Question[];
  children?: (relatedQuestions: React.ReactNode) => React.ReactNode;
};
export const RelatedQuestionVisibility: React.FC<RelatedProps> = (props) => {
  return (
    <WithFeatureFlags featureFlags={["question-visibility-admin"]}>
      {RelatedQuestionVisibilityInternal(props)}
    </WithFeatureFlags>
  );
};
const RelatedQuestionVisibilityInternal: React.FC<RelatedProps> = ({
  questionId,
  answerId,
  otherQuestions,
  children,
}) => {
  const relatedQuestions = findRelatedQuestionVisibilityByAnswerId(
    otherQuestions,
    questionId,
    answerId
  );

  if (relatedQuestions.length === 0) {
    return null;
  }

  const relatedQuestionsElement = (
    <>
      {relatedQuestions.map((q, index) => {
        const isLast = relatedQuestions.length - 1 === index;
        let conjunction: React.ReactNode = null;
        if (index > 0) {
          conjunction = isLast ? ", and " : ", ";
        }
        return (
          <React.Fragment key={q.id}>
            {conjunction}
            <Link
              color="primary.500"
              textDecoration="underline"
              onClick={() => {
                document
                  .getElementById(q.id)
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              {q.question}
            </Link>
          </React.Fragment>
        );
      })}
    </>
  );

  if (children !== undefined) {
    return children(relatedQuestionsElement);
  }

  return relatedQuestionsElement;
};

type Option = {
  id: string;
  label: string;
};
